// «Скидка OSMIX»: промокоды площадки в офлайн-моке. Зеркалит gateway (миграции
// 056–057): сид OSMIX10 = 10 %, лимит на аккаунт 1. used_count растёт на боевом
// бэке при оплате — в моке остаётся демонстрационным.
import { sleep } from "@/lib/utils";

import type {
	Api,
	Partner,
	PartnerSettings,
	PersonalOffersSettings,
	PromoCode,
	PromoCodeInput,
	PromoStats,
	PromoStatsTotals,
} from "../types";

// Поля механик П2 (миграции 320, 323) — у кода площадки по умолчанию.
const p2Defaults = {
	shopId: null,
	shopName: null,
	productIds: [] as string[],
	userId: null,
	isPublic: false,
	restoreOnCancel: true,
	personalRule: null,
};

const store = new Map<string, PromoCode>([
	[
		"OSMIX10",
		{
			code: "OSMIX10",
			kind: "percent",
			value: 0.1,
			minSubtotal: 0,
			maxUses: null,
			perUserLimit: 1,
			usedCount: 0,
			active: true,
			description: "Скидка OSMIX",
			startsAt: null,
			expiresAt: null,
			createdAt: new Date("2026-09-09T00:00:00.000Z").toISOString(),
			audience: "buyer",
			partnerId: null,
			partner: null,
			rewardKind: "none",
			rewardValue: 0,
			firstOrderOnly: false,
			newToShopOnly: false,
			shopIds: [],
			categoryIds: [],
			state: "active",
			archivedAt: null,
			...p2Defaults,
		},
	],
]);

const norm = (code: string) => code.trim().toUpperCase();

// Партнёры в офлайн-моке: статистики нет (она считается шлюзом из заказов),
// карточки показывают нули — чтобы экран открывался без бэкенда.
const partnerStore = new Map<string, Partner>();
let settings: PartnerSettings = { sellerBindDays: 30 };

const zeroTotals = (): PromoStatsTotals => ({
	visits: 0,
	applies: 0,
	orders: 0,
	buyers: 0,
	newBuyers: 0,
	repeatBuyers: 0,
	gmv: 0,
	discount: 0,
	revenue: 0,
	avgCheck: 0,
	refunds: 0,
	refundAmount: 0,
	itemsSold: 0,
	conversion: null,
	attractedBuyers: 0,
	attractedGmv: 0,
	sellersRegistered: 0,
	sellersApproved: 0,
	sellersStarted: 0,
	sellerProducts: 0,
	sellerOrders: 0,
	sellerQty: 0,
	sellerGmv: 0,
	sellerCommission: 0,
	reward: 0,
});

const today = () => new Date().toISOString().slice(0, 10);

const emptyStats = (p: PromoCode, period: { from?: string; to?: string } = {}): PromoStats => ({
	code: p.code,
	audience: p.audience,
	from: period.from || p.createdAt.slice(0, 10),
	to: period.to || today(),
	totals: zeroTotals(),
	daily: [],
	orders: [],
	buyers: [],
	products: [],
	shops: [],
	sellers: [],
});

const offRule = { enabled: false, kind: "percent" as const, value: 0.05, minSubtotal: 0, validDays: 7, periodDays: 30, thresholdDays: 3 };
let personalSettings: PersonalOffersSettings = {
	rules: {
		abandoned_cart: offRule,
		favorite_price_drop: { ...offRule, thresholdDays: 2 },
		inactive_buyer: { ...offRule, value: 0.1, thresholdDays: 60, validDays: 14, periodDays: 60 },
	},
};

export const promos: Api["promos"] = {
	list: async () => {
		await sleep(80);
		return [...store.values()].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
	},
	create: async (input: PromoCodeInput) => {
		await sleep(120);
		const code = norm(input.code);
		if (!code) throw new Error("Укажите код промокода");
		if (store.has(code)) throw new Error("Такой промокод уже существует");
		if (input.startsAt && input.expiresAt && input.expiresAt <= input.startsAt)
			throw new Error("Дата окончания раньше даты начала");
		const promo: PromoCode = {
			...p2Defaults,
			...input,
			productIds: input.productIds ?? [],
			isPublic: input.isPublic ?? false,
			restoreOnCancel: input.restoreOnCancel ?? true,
			newToShopOnly: input.newToShopOnly ?? false,
			code,
			usedCount: 0,
			createdAt: new Date().toISOString(),
			partner: partnerStore.get(input.partnerId ?? "") ?? null,
			state: input.active ? "active" : "inactive",
			archivedAt: null,
		};
		store.set(code, promo);
		return promo;
	},
	update: async (code: string, patch: Partial<PromoCodeInput> & { archived?: boolean }) => {
		await sleep(120);
		const existing = store.get(norm(code));
		if (!existing) throw new Error("Промокод не найден");
		const { code: _ignore, archived, ...rest } = patch;
		const updated: PromoCode = {
			...existing,
			...rest,
			archivedAt: archived === undefined ? existing.archivedAt : archived ? new Date().toISOString() : null,
		};
		updated.state = updated.archivedAt ? "archived" : updated.active ? "active" : "inactive";
		store.set(existing.code, updated);
		return updated;
	},
	remove: async (code: string) => {
		await sleep(100);
		if (!store.delete(norm(code))) throw new Error("Промокод не найден");
	},
	get: async (code) => {
		const p = store.get(norm(code));
		if (!p) throw new Error("Промокод не найден");
		return p;
	},
	stats: async (code, period) => {
		const p = store.get(norm(code));
		if (!p) throw new Error("Промокод не найден");
		return emptyStats(p, period);
	},
	exportStats: async (code, _period, format) => ({
		blob: new Blob([""], { type: format === "csv" ? "text/csv" : "application/octet-stream" }),
		filename: `Промокод ${norm(code)}.${format}`,
		rows: 0,
		skipped: 0,
	}),
	check: async (code, audience) => {
		const p = store.get(norm(code));
		if (!p) return { valid: false, code: norm(code), audience: null, partnerName: null, reason: "not_found", message: "Такого промокода нет" };
		if (p.audience !== audience)
			return { valid: false, code: p.code, audience: p.audience, partnerName: null, reason: "wrong_audience", message: "Промокод другой аудитории" };
		return { valid: true, code: p.code, audience: p.audience, partnerName: p.partner?.name ?? null, reason: null, message: "Промокод принят" };
	},
	settings: async () => settings,
	updateSettings: async (input) => {
		settings = input;
		return settings;
	},
	sellerBinding: async () => ({ bound: null, canBind: true, bindUntil: null }),
	personalOffers: async () => ({ settings: personalSettings, lastRun: null, codes: [] }),
	updatePersonalOffers: async (input) => {
		personalSettings = input;
		return personalSettings;
	},
	runPersonalOffers: async () => ({ created: 0, byRule: {}, ranAt: new Date().toISOString() }),
	bindSeller: async (_userId, promo) => ({
		bound: promo ? { code: norm(promo), partnerName: null, boundAt: new Date().toISOString(), source: "registration" } : null,
		canBind: !promo,
		bindUntil: null,
	}),
};

export const partners: Api["partners"] = {
	list: async () => [...partnerStore.values()],
	detail: async (id) => {
		const partner = partnerStore.get(id);
		if (!partner) throw new Error("Партнёр не найден");
		const list = [...store.values()].filter((p) => p.partnerId === id);
		return { partner, promos: list, stats: { from: today(), to: today(), totals: zeroTotals(), byPromo: [] } };
	},
	create: async (input) => {
		const now = new Date().toISOString();
		const partner: Partner = { ...input, id: `prt_${Date.now()}`, user: null, promoCount: 0, createdAt: now, updatedAt: now };
		partnerStore.set(partner.id, partner);
		return partner;
	},
	update: async (id, patch) => {
		const existing = partnerStore.get(id);
		if (!existing) throw new Error("Партнёр не найден");
		const updated: Partner = { ...existing, ...patch, updatedAt: new Date().toISOString() } as Partner;
		partnerStore.set(id, updated);
		return updated;
	},
	remove: async (id) => {
		partnerStore.delete(id);
	},
};
